import { format } from "date-fns";
import { Typography } from "@/components/atoms";

/**
 * DRIVER ARRIVES — the pickup a flight booking is about to commit, stated (DSN, "add a visual pickup
 * time").
 *
 * The panel's flight mode derives the pickup from two operands the client can see — the landing on the
 * flight card, and the minutes in "Delay driver arrival by" — and until now never printed the answer.
 * The reader was left to do `1:25 PM + 30` themselves, on the one value the whole step exists to decide.
 *
 * It is a STATEMENT, not a field, and that is the reason this can exist here at all. `SuggestedPickupCard`
 * on the drop-off side deliberately refuses to restate its time, because there the suggestion is
 * pre-filled into an editable picker and a card beside a field holding a different value is the
 * disclosure defect DEV-1510 removed. Nothing is editable on the arrival side: the pickup is not a field,
 * it is the output of the two inputs above it, so naming it adds a fact rather than a second source of
 * truth.
 *
 * Purely presentational, and deliberately given a `Date` rather than a flight and a delay — the caller
 * takes the value from `resolvePickup`, the same call `handleProceed` commits from, so the number on
 * screen and the number booked cannot drift apart. A component that recomputed `landing + delay` for
 * itself would be a second derivation to keep in step, and the anchored branch (a booked pickup handed
 * back untouched) is not that arithmetic at all.
 *
 * ## Formatted browser-local, on purpose
 *
 * Both of `resolvePickup`'s flight branches speak the commit convention: an instant whose BROWSER-LOCAL
 * fields hold the pickup's wall clock at the booking location (see `flightPickupCommitValue`). So this
 * formats with plain `date-fns`, NOT the `formatFlightTime` family — those read an offset off the ISO and
 * are for airport-local flight times, and pointing them at a commit value would re-interpret an
 * already-converted wall clock and land the card an offset away from the booking it describes.
 */
export const DriverArrivesCard = ({ pickupAt }: { pickupAt: Date }) => (
  <div className="flex flex-col items-center gap-0.5 rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-3">
    <Typography variant="caption" className="text-neutral-dark-gray">
      Driver Arrives
    </Typography>
    {/* The panel's own scale for a time this size — the manual hour/minute inputs are `text-2xl font-bold
        text-neutral-black`, and this replaces them on the flight path. No `max-lg` step down: those
        shrink because three of them share a row, while this is full width at every breakpoint.

        `title` for its `<span>`, then both of its type classes overridden — the `!` prefix because the
        variant's own `text-lg`/`max-lg:text-lg` and `font-semibold` would otherwise win or tie on
        stylesheet order rather than on intent. */}
    <Typography variant="title" className="!text-2xl !font-bold text-neutral-black">
      {format(pickupAt, "h:mm a")}
    </Typography>
    {/* The date earns its place here even though the flight card above carries one: an arrival late enough
        pushes the pickup onto the FOLLOWING day, which is exactly when a reader needs to be told. */}
    <Typography variant="small-regular" className="text-neutral-silver-gray">
      {format(pickupAt, "d MMM yyyy")}
    </Typography>
  </div>
);
